import { Link } from "react-router-dom";
import {
  ArrowRight, Gift, Award, Gamepad2, Check, Store, Building2, Users,
  HeartHandshake, MonitorPlay, Trophy,
} from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSEO } from "@/hooks/useSEO";

const solutions = [
  {
    icon: Gift,
    title: "Digital Gift Cards",
    body: "Deliver branded digital gift cards from the world's leading retail, entertainment, lifestyle, and shopping brands.",
    points: [
      "Hundreds of international retail & lifestyle brands",
      "Fixed and flexible denominations",
      "Branded delivery via email and SMS",
      "Instant digital fulfillment within seconds",
    ],
  },
  {
    icon: Award,
    title: "Corporate Rewards",
    body: "Motivate employees, reward customers, and strengthen loyalty with personalized digital gifting campaigns.",
    points: [
      "Employee recognition & milestone programs",
      "Customer loyalty and retention campaigns",
      "Bulk ordering with scheduled delivery",
      "Curated catalogs matched to your audience",
    ],
  },
  {
    icon: Gamepad2,
    title: "Gaming Vouchers",
    body: "Access popular gaming subscriptions, in-game currency, and entertainment vouchers with reliable fulfillment.",
    points: [
      "Steam, Xbox, PlayStation, Nintendo & Minecraft",
      "Gaming subscriptions and in-game currency",
      "Entertainment and streaming vouchers",
      "Consistent, reliable enterprise fulfillment",
    ],
  },
];

const industries = [
  { icon: Store, label: "Retail & Ecommerce" },
  { icon: Building2, label: "Corporate Enterprises" },
  { icon: Users, label: "Employee Rewards" },
  { icon: HeartHandshake, label: "Customer Loyalty" },
  { icon: MonitorPlay, label: "Gaming Platforms" },
  { icon: Trophy, label: "Rewards Programs" },
];

export default function Solutions() {
  useSEO(
    "Smart Digital Reward Solutions — Vowpin Global",
    "Digital gift cards, corporate rewards, and gaming vouchers delivered instantly across 120+ countries — premium digital gifting for every business moment."
  );
  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c98a00]">Solutions</p>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
              Smart Digital <span className="text-gradient-blue">Reward Solutions</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg">
              Premium digital gifting for every business moment — delivered instantly across 120+ countries.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="solutions-talk-to-sales-btn"
                className="group flex items-center gap-2 rounded-full bg-[#0816a8] px-8 py-4 text-sm font-semibold text-white shadow-[0_10px_28px_-8px_rgba(8,22,168,0.5)] transition-all duration-300 hover:bg-[#0a1bc4] hover:shadow-[0_14px_36px_-8px_rgba(8,22,168,0.6)]"
              >
                Talk to Sales
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                data-testid="solutions-view-catalog-btn"
                className="rounded-full border border-[#d7dce8] bg-white px-8 py-4 text-sm font-semibold text-[#374151] transition-colors duration-300 hover:border-[#0816a8]/50 hover:text-[#0816a8]"
              >
                View Catalog
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Solution detail cards */}
      <section className="border-y border-[#e8ecf4] bg-[#f7f9fc] py-24">
        <div className="mx-auto max-w-7xl space-y-8 px-6 lg:px-10">
          {solutions.map((s, i) => (
            <Reveal key={s.title} delay={0.05}>
              <div
                className={`glow-card grid gap-8 rounded-[24px] p-10 lg:grid-cols-2 lg:items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                data-testid={`solution-detail-${i}`}
              >
                <div>
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0816a8]/[0.06] ring-1 ring-[#0816a8]/15">
                    <s.icon className="h-7 w-7 text-[#0816a8]" />
                  </div>
                  <h2 className="mt-6 font-display text-2xl font-semibold text-[#111827] sm:text-3xl">{s.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-[#64748b]">{s.body}</p>
                </div>
                <ul className="space-y-3.5">
                  {s.points.map((p, j) => (
                    <li key={p} className="flex items-center gap-4 rounded-xl border border-[#e8ecf4] bg-[#f7f9fc] px-5 py-3.5" data-testid={`solution-${i}-point-${j}`}>
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#f4a900]/[0.15]">
                        <Check className="h-3.5 w-3.5 text-[#c98a00]" />
                      </span>
                      <span className="text-sm font-medium text-[#374151]">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Industries */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Industries"
            title="Industries We Serve"
            sub="Trusted by teams who gift, reward, and recognize at scale."
          />
          <div className="mt-12 flex flex-wrap gap-4">
            {industries.map((ind, i) => (
              <Reveal key={ind.label} delay={i * 0.05}>
                <div
                  className="flex items-center gap-3 rounded-full border border-[#e5e9f2] bg-white px-6 py-3.5 transition-colors duration-300 hover:border-[#0816a8]/40"
                  data-testid={`solutions-industry-${i}`}
                >
                  <ind.icon className="h-5 w-5 text-[#0816a8]" />
                  <span className="text-sm font-semibold text-[#374151]">{ind.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to launch your gifting program?"
        sub="Tell us your audience and markets — we'll curate a catalog and have your first campaign live in days."
      />
    </main>
  );
}
